import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import api from '../services/api';
import { useInsufficientCredits } from '../contexts/InsufficientCreditsContext';

/**
 * Hook for fetching postal addresses for campaign contacts.
 *
 * Mirrors useContactEnrichment: credit-gated bulk action that posts to the
 * address endpoint and refreshes the table on completion. Addresses are billed
 * per resolved contact (cache hit or fresh Versium call); contacts without a
 * phone or already unlocked in this campaign are skipped server-side.
 *
 * @param {string} campaignId - Campaign ID
 * @param {string} versionId - Active version ID
 * @param {Function} onComplete - Callback when the fetch completes (refetch data)
 * @param {Function} withCreditWarning - Optional credit warning wrapper
 * @param {Object} listRef - Optional ref to the virtualized list for scroll control
 * @returns {Object} - Address fetch state and operations
 */
const useContactAddress = (campaignId, versionId, onComplete, withCreditWarning = null, listRef = null) => {
  const [isFetchingAddresses, setIsFetchingAddresses] = useState(false);
  const toast = useToast();
  const { showInsufficientCreditsModal } = useInsufficientCredits();

  /**
   * Fetch addresses for contacts.
   *
   * @param {Array<string>} contactIds - Optional elasticsearch_ids to scope to.
   *                                      If null, fetches for all eligible contacts.
   */
  const fetchAddresses = useCallback(async (contactIds = null) => {
    if (!campaignId || !versionId) {
      toast({
        title: 'Error',
        description: 'Campaign or version information is missing',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const performFetch = async () => {
      try {
        const response = await api.post(
          `/api/enrichment/campaigns/${campaignId}/contacts/fetch-address`,
          {
            version_id: versionId,
            contact_ids: contactIds,
          }
        );

        if (response.status === 403 || response.requiresSignup) {
          return;
        }

        const { resolved, matched, no_match, failed } = response.data;

        let description = `Found ${matched} address${matched === 1 ? '' : 'es'}`;
        if (no_match > 0) description += `, ${no_match} with no match`;
        if (failed > 0) description += `, ${failed} could not be fetched (retry later)`;

        toast({
          title: resolved > 0 ? 'Addresses Updated' : 'No Addresses Fetched',
          description,
          status: resolved > 0 ? 'success' : 'info',
          duration: 5000,
          isClosable: true,
        });

        // Refresh header credit count (shared event with enrichment).
        window.dispatchEvent(new Event('enrichmentCompleted'));

        if (onComplete) {
          await onComplete();
          if (listRef?.current) {
            setTimeout(() => {
              listRef.current?.scrollToItem?.(0, 'start');
            }, 0);
          }
        }

        return response.data;
      } catch (error) {
        console.error('Error fetching contact addresses:', error);

        if (error.response?.status === 402) {
          const errorDetail = error.response?.data?.detail || 'You do not have enough credits to fetch addresses.';
          showInsufficientCreditsModal(errorDetail);
        } else if (error.response?.status === 403) {
          // Signup modal handled by API interceptor.
        } else {
          toast({
            title: 'Address Fetch Failed',
            description: error.response?.data?.detail || 'Failed to fetch addresses',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }

        throw error;
      }
    };

    setIsFetchingAddresses(true);
    try {
      if (withCreditWarning) {
        return await withCreditWarning(
          'contact_address',
          {
            campaign_id: campaignId,
            version_id: versionId,
            contact_ids: contactIds,
          },
          performFetch
        );
      }
      return await performFetch();
    } finally {
      setIsFetchingAddresses(false);
    }
  }, [campaignId, versionId, toast, onComplete, showInsufficientCreditsModal, withCreditWarning, listRef]);

  return {
    isFetchingAddresses,
    fetchAddresses,
  };
};

export default useContactAddress;
